import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Calendar,
    Edit3,
    Image as ImageIcon,
    Loader2,
    MapPin,
    Plus,
    Save,
    Trash2,
    UserRound,
    X
} from 'lucide-react';
import { DELETE, FORM_POST, GET, POST, PUT } from '../services/api';

const LIST_LIMIT = 10000;

const DATE_RANGE_OPTIONS = [
    'This Week',
    'Last 1 Months',
    'Last 3 Months',
    'Last 6 Months',
    'Last 12 Months',
    'All Time'
];

const MODE_CONFIG = {
    'business-given': {
        title: 'Business Given Details',
        listEndpoint: (memberId, dateRange, city, chapter, tenureId) => {
            const params = new URLSearchParams({
                page: '1',
                limit: String(LIST_LIMIT),
                direction: 'given',
                dateRange
            });
            if (city) params.set('city', city);
            if (chapter) params.set('chapter', chapter);
            if (tenureId) params.set('tenureId', tenureId);
            return `/networking/business-exchanges/${memberId}?${params.toString()}`;
        },
        createEndpoint: '/networking/business-exchange',
        updateEndpoint: (id) => `/networking/business-exchange/${id}`,
        deleteEndpoint: (id) => `/networking/business-exchange/${id}`,
        fixedField: 'from_member_id',
        counterPartyField: 'to_member_id'
    },
    'business-received': {
        title: 'Business Received Details',
        listEndpoint: (memberId, dateRange, city, chapter, tenureId) => {
            const params = new URLSearchParams({
                page: '1',
                limit: String(LIST_LIMIT),
                direction: 'received',
                dateRange
            });
            if (city) params.set('city', city);
            if (chapter) params.set('chapter', chapter);
            if (tenureId) params.set('tenureId', tenureId);
            return `/networking/business-exchanges/${memberId}?${params.toString()}`;
        },
        createEndpoint: '/networking/business-exchange',
        updateEndpoint: (id) => `/networking/business-exchange/${id}`,
        deleteEndpoint: (id) => `/networking/business-exchange/${id}`,
        fixedField: 'to_member_id',
        counterPartyField: 'from_member_id'
    },
    'reference-given': {
        title: 'Reference Given Details',
        listEndpoint: (memberId, dateRange, city, chapter, tenureId) => {
            const params = new URLSearchParams({
                page: '1',
                limit: String(LIST_LIMIT),
                direction: 'given',
                dateRange
            });
            if (city) params.set('city', city);
            if (chapter) params.set('chapter', chapter);
            if (tenureId) params.set('tenureId', tenureId);
            return `/networking/reference-passes/${memberId}?${params.toString()}`;
        },
        createEndpoint: '/networking/reference-pass',
        updateEndpoint: (id) => `/networking/reference-pass/${id}`,
        deleteEndpoint: (id) => `/networking/reference-pass/${id}`,
        fixedField: 'from_member_id',
        counterPartyField: 'to_member_id'
    },
    'reference-received': {
        title: 'Reference Received Details',
        listEndpoint: (memberId, dateRange, city, chapter, tenureId) => {
            const params = new URLSearchParams({
                page: '1',
                limit: String(LIST_LIMIT),
                direction: 'received',
                dateRange
            });
            if (city) params.set('city', city);
            if (chapter) params.set('chapter', chapter);
            if (tenureId) params.set('tenureId', tenureId);
            return `/networking/reference-passes/${memberId}?${params.toString()}`;
        },
        createEndpoint: '/networking/reference-pass',
        updateEndpoint: (id) => `/networking/reference-pass/${id}`,
        deleteEndpoint: (id) => `/networking/reference-pass/${id}`,
        fixedField: 'to_member_id',
        counterPartyField: 'from_member_id'
    },
    'one-to-one': {
        title: 'One-to-One Details',
        listEndpoint: (memberId, dateRange, city, chapter, tenureId) => {
            const params = new URLSearchParams({
                page: '1',
                limit: String(LIST_LIMIT),
                dateRange
            });
            if (city) params.set('city', city);
            if (chapter) params.set('chapter', chapter);
            if (tenureId) params.set('tenureId', tenureId);
            return `/networking/personal-meetings/${memberId}?${params.toString()}`;
        },
        createEndpoint: '/networking/personal-meeting',
        updateEndpoint: (id) => `/networking/personal-meeting/${id}`,
        deleteEndpoint: (id) => `/networking/personal-meeting/${id}`,
        fixedField: 'host_id',
        counterPartyField: 'visitor_id'
    }
};

const safeDate = (date) => {
    if (!date) return '-';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const fallbackMemberLabel = (member) => {
    if (!member) return 'Unknown member';
    return member.business?.full_name || member.email || member._id || 'Unknown member';
};

const getInitials = (name) => {
    if (!name) return 'NA';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return 'NA';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

// Given/received modes share one form: to_member_* holds the other member (receiver
// for *-given, giver for *-received); handleSubmit puts it on the right side.
const isBusinessType = (type) => type === 'business-given' || type === 'business-received';
const isReferenceType = (type) => type === 'reference-given' || type === 'reference-received';
const isReceivedType = (type) => type === 'business-received' || type === 'reference-received';

const getInitialFormState = (type, memberId) => {
    if (isBusinessType(type)) {
        return {
            from_member_id: memberId,
            to_member_id: '',
            to_member_name: '',
            amount: '',
            date: new Date().toISOString().split('T')[0],
            remarks: ''
        };
    }

    if (isReferenceType(type)) {
        return {
            from_member_id: memberId,
            to_member_id: '',
            to_member_name: '',
            refName: '',
            phone: '',
            date: new Date().toISOString().split('T')[0],
            remarks: ''
        };
    }

    return {
        host_id: memberId,
        visitor_id: '',
        visitor_name: '',
        location: 'my place',
        address: '',
        latitude: '',
        longitude: '',
        meeting_date: new Date().toISOString().split('T')[0],
        remarks: ''
    };
};

const ChapterNetworkingDetail = () => {
    const { memberId, type } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const config = MODE_CONFIG[type];

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedDateRange, setSelectedDateRange] = useState('Last 3 Months');
    const [selectedTenureId, setSelectedTenureId] = useState('');
    const [selectedCityId, setSelectedCityId] = useState('');
    const [selectedChapterId, setSelectedChapterId] = useState('');
    const [selectedCityName, setSelectedCityName] = useState('');
    const [selectedChapterName, setSelectedChapterName] = useState('');
    const currentMemberName = searchParams.get('memberName') || 'Member';
    // Custom start/end dates carried over from the chapter report (overrides Date Range).
    const customStart = searchParams.get('startDate') || '';
    const customEnd = searchParams.get('endDate') || '';

    const [cities, setCities] = useState([]);
    const [chapters, setChapters] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({});

    const [memberResults, setMemberResults] = useState([]);
    const [memberSearchLoading, setMemberSearchLoading] = useState(false);
    const [showMemberDropdown, setShowMemberDropdown] = useState(false);

    const [meetingImageFile, setMeetingImageFile] = useState(null);
    const [meetingImagePreview, setMeetingImagePreview] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        const dateRange = searchParams.get('dateRange') || 'Last 3 Months';
        const cityId = searchParams.get('city') || '';
        const chapterId = searchParams.get('chapter') || '';
        const tenureId = searchParams.get('tenureId') || '';

        setSelectedDateRange(dateRange);
        setSelectedTenureId(tenureId);
        setSelectedCityId(cityId);
        setSelectedChapterId(chapterId);

        const city = cities.find(c => c._id === cityId);
        const chapter = chapters.find(c => c._id === chapterId);
        setSelectedCityName(city?.name || '');
        setSelectedChapterName(chapter?.name || '');
    }, [searchParams, cities, chapters]);

    useEffect(() => {
        if (!config || !memberId) return;
        setFormData(getInitialFormState(type, memberId));
        setEditingRow(null);
        setMeetingImageFile(null);
        setMeetingImagePreview('');
    }, [config, memberId, type]);

    useEffect(() => {
        return () => {
            if (meetingImagePreview && meetingImagePreview.startsWith('blob:')) {
                URL.revokeObjectURL(meetingImagePreview);
            }
        };
    }, [meetingImagePreview]);

    const fetchCities = useCallback(async () => {
        try {
            const response = await GET('/networking/cities');
            if (response?.success && Array.isArray(response.data)) {
                setCities(response.data);
            } else {
                setCities([]);
            }
        } catch {
            setCities([]);
        }
    }, []);

    const fetchChapters = useCallback(async (cityId) => {
        if (!cityId) {
            setChapters([]);
            return;
        }
        try {
            const response = await GET(`/networking/chapters/${cityId}`);
            if (response?.success && Array.isArray(response.data)) {
                setChapters(response.data);
            } else {
                setChapters([]);
            }
        } catch {
            setChapters([]);
        }
    }, []);

    const fetchItems = useCallback(async () => {
        if (!config || !memberId) return;
        setLoading(true);
        try {
            let url = config.listEndpoint(memberId, selectedDateRange, selectedCityId, selectedChapterId, selectedTenureId);
            if (customStart && customEnd) url += `&qStart=${customStart}&qEnd=${customEnd}`;
            const response = await GET(url);
            const apiRows = Array.isArray(response?.data) ? response.data : [];
            setItems(apiRows);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [config, memberId, selectedDateRange, selectedTenureId, selectedCityId, selectedChapterId, customStart, customEnd]);

    useEffect(() => { fetchCities(); }, [fetchCities]);
    useEffect(() => { fetchChapters(selectedCityId); }, [fetchChapters, selectedCityId]);
    useEffect(() => { fetchItems(); }, [fetchItems]);

    const searchMembers = useCallback(async (query) => {
        const q = query?.trim();
        if (!q || q.length < 2) {
            setMemberResults([]);
            return;
        }
        setMemberSearchLoading(true);
        try {
            const params = new URLSearchParams();
            params.set('search', q);
            if (type !== 'one-to-one') {
                if (selectedCityId) params.set('cityId', selectedCityId);
                if (selectedChapterId) params.set('chapterId', selectedChapterId);
            }
            const response = await GET(`/networking/search?${params.toString()}`);
            if (response?.success && Array.isArray(response.data)) {
                setMemberResults(response.data.filter((member) => member.user_id !== memberId));
            } else {
                setMemberResults([]);
            }
        } catch {
            setMemberResults([]);
        } finally {
            setMemberSearchLoading(false);
        }
    }, [memberId, selectedChapterId, selectedCityId, type]);

    const activeSearchValue = useMemo(() => {
        if (isBusinessType(type)) return formData.to_member_name || '';
        if (isReferenceType(type)) return formData.to_member_name || '';
        return formData.visitor_name || '';
    }, [formData, type]);

    useEffect(() => {
        if (!showModal) return;
        const timer = setTimeout(() => {
            searchMembers(activeSearchValue);
        }, 350);
        return () => clearTimeout(timer);
    }, [activeSearchValue, searchMembers, showModal]);

    const openCreateModal = () => {
        setEditingRow(null);
        setFormData(getInitialFormState(type, memberId));
        setShowModal(true);
        setMemberResults([]);
        setShowMemberDropdown(false);
        setMeetingImageFile(null);
        setMeetingImagePreview('');
    };

    const openEditModal = (row) => {
        setEditingRow(row);

        if (isBusinessType(type)) {
            const other = isReceivedType(type) ? row?.from : row?.to;
            setFormData({
                from_member_id: row?.from?._id || memberId,
                to_member_id: other?._id || '',
                to_member_name: fallbackMemberLabel(other),
                amount: row?.amount || '',
                date: row?.date
                    ? new Date(row.date).toISOString().split('T')[0]
                    : (row?.created_at ? new Date(row.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
                remarks: row?.remarks || ''
            });
        }

        if (isReferenceType(type)) {
            const other = isReceivedType(type) ? row?.from : row?.to;
            setFormData({
                from_member_id: row?.from?._id || memberId,
                to_member_id: other?._id || '',
                to_member_name: fallbackMemberLabel(other),
                refName: row?.refName || '',
                phone: row?.phone || '',
                date: row?.date ? new Date(row.date).toISOString().split('T')[0] : '',
                remarks: row?.remarks || ''
            });
        }

        if (type === 'one-to-one') {
            setFormData({
                host_id: row?.host?._id || memberId,
                visitor_id: row?.visitor?._id || '',
                visitor_name: fallbackMemberLabel(row?.visitor),
                location: row?.location || 'my place',
                address: row?.address || '',
                latitude: row?.latitude || '',
                longitude: row?.longitude || '',
                meeting_date: row?.meeting_date ? new Date(row.meeting_date).toISOString().split('T')[0] : '',
                remarks: row?.remarks || ''
            });
            setMeetingImagePreview(row?.image || '');
            setMeetingImageFile(null);
        }

        setShowModal(true);
        setMemberResults([]);
        setShowMemberDropdown(false);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingRow(null);
        setMemberResults([]);
        setShowMemberDropdown(false);
        setMeetingImageFile(null);
        setMeetingImagePreview('');
        setFormData(getInitialFormState(type, memberId));
    };

    const handleMemberSelect = (member) => {
        if (isBusinessType(type) || isReferenceType(type)) {
            setFormData((prev) => ({
                ...prev,
                to_member_id: member.user_id,
                to_member_name: member.full_name
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                visitor_id: member.user_id,
                visitor_name: member.full_name
            }));
        }
        setShowMemberDropdown(false);
        setMemberResults([]);
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (meetingImagePreview && meetingImagePreview.startsWith('blob:')) {
            URL.revokeObjectURL(meetingImagePreview);
        }
        setMeetingImageFile(file);
        setMeetingImagePreview(URL.createObjectURL(file));
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            await DELETE(config.deleteEndpoint(id));
            fetchItems();
        } catch (error) {
            alert(error?.message || 'Delete failed');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);

        try {
            if (isBusinessType(type)) {
                const received = isReceivedType(type);
                const payload = {
                    from_member_id: received ? formData.to_member_id : memberId,
                    to_member_id: received ? memberId : formData.to_member_id,
                    remarks: formData.remarks,
                    amount: formData.amount,
                    date: formData.date
                };
                if (!formData.to_member_id || !payload.amount) {
                    alert('Please fill all required fields.');
                    setSubmitting(false);
                    return;
                }
                if (editingRow?._id) {
                    await PUT(config.updateEndpoint(editingRow._id), payload);
                } else {
                    await POST(config.createEndpoint, payload);
                }
            }

            if (isReferenceType(type)) {
                const received = isReceivedType(type);
                const payload = {
                    from_member_id: received ? formData.to_member_id : memberId,
                    to_member_id: received ? memberId : formData.to_member_id,
                    refName: formData.refName,
                    phone: formData.phone,
                    date: formData.date,
                    remarks: formData.remarks
                };
                if (!formData.to_member_id) {
                    alert('Please fill all required fields.');
                    setSubmitting(false);
                    return;
                }
                if (editingRow?._id) {
                    await PUT(config.updateEndpoint(editingRow._id), payload);
                } else {
                    await POST(config.createEndpoint, payload);
                }
            }

            if (type === 'one-to-one') {
                const payload = new FormData();
                payload.append('host_id', memberId);
                payload.append('visitor_id', formData.visitor_id);
                payload.append('remarks', formData.remarks);
                payload.append('location', formData.location);
                payload.append('meeting_date', formData.meeting_date);
                if (formData.location === 'other') {
                    payload.append('address', formData.address);
                    payload.append('latitude', formData.latitude || '');
                    payload.append('longitude', formData.longitude || '');
                }
                if (meetingImageFile) {
                    payload.append('one_to_one_meeting_image', meetingImageFile);
                }
                if (!formData.visitor_id || !formData.location) {
                    alert('Please fill all required fields.');
                    setSubmitting(false);
                    return;
                }
                if (formData.location === 'other' && !formData.address) {
                    alert('Address is required for Other location.');
                    setSubmitting(false);
                    return;
                }
                if (editingRow?._id) {
                    await FORM_POST(config.updateEndpoint(editingRow._id), payload, 'PUT');
                } else {
                    await FORM_POST(config.createEndpoint, payload, 'POST');
                }
            }

            closeModal();
            fetchItems();
        } catch (error) {
            alert(error?.message || 'Save failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (!config) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-slate-600">
                Invalid report type.
            </div>
        );
    }

    return (
        <div>
            <div className="space-y-5">
                {/* Header */}
                <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Chapter Report Details</div>
                            <h1 className="text-2xl font-black text-slate-800 mt-1">{config.title}</h1>
                            <p className="text-sm text-slate-500 mt-1">
                                Member: <span className="font-semibold text-slate-700">{currentMemberName}</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
                                <div className="min-w-[190px]">
                                    <label className="block text-xs font-bold text-slate-500 mb-1">Date Range</label>
                                    <select
                                        disabled={Boolean(selectedTenureId)}
                                        value={selectedDateRange}
                                        onChange={(event) => {
                                            setSelectedDateRange(event.target.value);
                                            setSearchParams(prev => {
                                                const newParams = new URLSearchParams(prev);
                                                newParams.set('dateRange', event.target.value);
                                                // Picking a named range here replaces the report's custom dates.
                                                newParams.delete('startDate');
                                                newParams.delete('endDate');
                                                if (selectedCityId) newParams.set('city', selectedCityId);
                                                if (selectedChapterId) newParams.set('chapter', selectedChapterId);
                                                if (selectedTenureId) newParams.set('tenureId', selectedTenureId);
                                                return newParams;
                                            });
                                        }}
                                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700"
                                    >
                                        {DATE_RANGE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                    {selectedTenureId ? (
                                        <p className="text-xs text-slate-400 mt-1">Scoped to the tenure selected on the report.</p>
                                    ) : customStart && customEnd ? (
                                        <p className="text-xs text-slate-400 mt-1">Custom dates: {customStart} to {customEnd}</p>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <span>City:</span>
                                    <span className="font-semibold text-slate-800">{selectedCityName || 'All Cities'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <span>Chapter:</span>
                                    <span className="font-semibold text-slate-800">{selectedChapterName || 'All Chapters'}</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.history.length > 1) {
                                        navigate(-1);
                                    } else {
                                        navigate('/admin/business-report');
                                    }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-50"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            <button
                                type="button"
                                onClick={openCreateModal}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
                            >
                                <Plus className="w-4 h-4" /> Add New
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                {type === 'one-to-one' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                        {loading && (
                            <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
                                Loading records...
                            </div>
                        )}
                        {!loading && items.length === 0 && (
                            <div className="col-span-full bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500">
                                No records found.
                            </div>
                        )}
                        {!loading && items.map((row) => (
                            <div key={row._id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="relative h-44 border-b border-slate-100 bg-slate-100">
                                    {row.image ? (
                                        <img src={row.image} alt="meeting" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full flex items-center justify-center text-slate-400 text-xs">
                                            No meeting image
                                        </div>
                                    )}
                                    <div className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/95 text-slate-700 border border-slate-200 shadow-sm">
                                        {safeDate(row.meeting_date)}
                                    </div>
                                </div>

                                <div className="p-4 space-y-3">
                                    <div className="grid grid-cols-3 gap-3 items-center border border-slate-100 rounded-xl p-3 bg-slate-50">
                                        <div className="text-center">
                                            {row?.host?.profile_picture ? (
                                                <img
                                                    src={row.host.profile_picture}
                                                    alt="host"
                                                    className="w-14 h-14 rounded-full object-cover border border-slate-200 mx-auto"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none';
                                                        const fallback = event.currentTarget.nextSibling;
                                                        if (fallback) fallback.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="w-14 h-14 rounded-full border border-slate-200 bg-blue-100 text-blue-700 font-bold text-sm items-center justify-center mx-auto"
                                                style={{ display: row?.host?.profile_picture ? 'none' : 'flex' }}
                                            >
                                                {getInitials(fallbackMemberLabel(row.host))}
                                            </div>
                                            <div className="text-xs font-semibold text-slate-700 mt-2 leading-tight">{fallbackMemberLabel(row.host)}</div>
                                            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mt-1">Host</div>
                                        </div>

                                        <div className="text-center">
                                            <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
                                                <UserRound className="w-4 h-4" />
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mt-2">Face to Face</div>
                                        </div>

                                        <div className="text-center">
                                            {row?.visitor?.profile_picture ? (
                                                <img
                                                    src={row.visitor.profile_picture}
                                                    alt="visitor"
                                                    className="w-14 h-14 rounded-full object-cover border border-slate-200 mx-auto"
                                                    onError={(event) => {
                                                        event.currentTarget.style.display = 'none';
                                                        const fallback = event.currentTarget.nextSibling;
                                                        if (fallback) fallback.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="w-14 h-14 rounded-full border border-slate-200 bg-emerald-100 text-emerald-700 font-bold text-sm items-center justify-center mx-auto"
                                                style={{ display: row?.visitor?.profile_picture ? 'none' : 'flex' }}
                                            >
                                                {getInitials(fallbackMemberLabel(row.visitor))}
                                            </div>
                                            <div className="text-xs font-semibold text-slate-700 mt-2 leading-tight">{fallbackMemberLabel(row.visitor)}</div>
                                            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400 mt-1">Visitor</div>
                                        </div>
                                    </div>

                                    <p className="text-sm text-slate-700">
                                        <span className="font-semibold">Remarks:</span> {row.remarks || '-'}
                                    </p>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-700">Address:</div>
                                        <p
                                            className="text-sm text-slate-600 leading-6"
                                            title={row.address || '-'}
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            {row.address || '-'}
                                        </p>
                                    </div>

                                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                                        <button onClick={() => openEditModal(row)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100">
                                            <Edit3 className="w-3.5 h-3.5" /> Edit
                                        </button>
                                        <button onClick={() => handleDelete(row._id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100">
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">From</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">To</th>
                                        {isBusinessType(type) && <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase">Amount</th>}
                                        {isReferenceType(type) && <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Ref Name</th>}
                                        {isReferenceType(type) && <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Phone</th>}
                                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Remarks</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                                        <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {loading && (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-10 text-center text-slate-500">Loading records...</td>
                                        </tr>
                                    )}
                                    {!loading && items.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-10 text-center text-slate-500">No records found.</td>
                                        </tr>
                                    )}
                                    {!loading && items.map((row) => (
                                        <tr key={row._id} className="hover:bg-slate-50/60">
                                            <td className="px-4 py-3 text-sm text-slate-700 font-semibold">{fallbackMemberLabel(row.from)}</td>
                                            <td className="px-4 py-3 text-sm text-slate-700">{fallbackMemberLabel(row.to)}</td>
                                            {isBusinessType(type) && <td className="px-4 py-3 text-sm text-right text-emerald-700 font-bold">{Number(row.amount || 0).toLocaleString('en-IN')}</td>}
                                            {isReferenceType(type) && <td className="px-4 py-3 text-sm text-slate-700">{row.refName || '-'}</td>}
                                            {isReferenceType(type) && <td className="px-4 py-3 text-sm text-slate-700">{row.phone || '-'}</td>}
                                            <td className="px-4 py-3 text-sm text-slate-700 max-w-[320px] truncate" title={row.remarks}>{row.remarks || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-slate-500">{safeDate(row.date || row.created_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex gap-2">
                                                    <button onClick={() => openEditModal(row)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100">
                                                        <Edit3 className="w-3.5 h-3.5" /> Edit
                                                    </button>
                                                    <button onClick={() => handleDelete(row._id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100">
                                                        <Trash2 className="w-3.5 h-3.5" /> Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-4">
                    <div className="text-sm text-slate-600">
                        Total Records: <span className="font-bold text-slate-800">{items.length}</span>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[1000] bg-slate-900/45 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-slate-200 shadow-2xl">
                        <div className="sticky top-0 bg-white z-10 p-5 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-xl font-black text-slate-800">{editingRow ? 'Edit Record' : 'Create Record'}</h3>
                            <button onClick={closeModal} className="p-1 rounded-full hover:bg-slate-100 text-slate-500">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-5 space-y-4">
                            {type !== 'one-to-one' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <span>City:</span>
                                        <span className="font-semibold text-slate-800">{selectedCityName || 'All Cities'}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                        <span>Chapter:</span>
                                        <span className="font-semibold text-slate-800">{selectedChapterName || 'All Chapters'}</span>
                                    </div>
                                </div>
                            )}

                            {/* Member search field */}
                            {type === 'one-to-one' ? (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Host Member</label>
                                        <div className="relative">
                                            <UserRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                            <input
                                                type="text"
                                                value={currentMemberName}
                                                readOnly
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-sm font-semibold text-slate-700"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Visitor Member *</label>
                                        <div className="relative">
                                            <UserRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                            <input
                                                type="text"
                                                value={formData.visitor_name}
                                                onChange={(event) => {
                                                    const value = event.target.value;
                                                    setFormData((prev) => ({ ...prev, visitor_name: value, visitor_id: '' }));
                                                    setShowMemberDropdown(true);
                                                }}
                                                onFocus={() => setShowMemberDropdown(true)}
                                                placeholder="Type visitor member name..."
                                                className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                            {memberSearchLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />}
                                        </div>
                                        {showMemberDropdown && memberResults.length > 0 && (
                                            <div className="mt-1 border border-slate-200 rounded-xl bg-white shadow-lg max-h-52 overflow-y-auto">
                                                {memberResults.map((member) => (
                                                    <button
                                                        key={member._id}
                                                        type="button"
                                                        onClick={() => handleMemberSelect(member)}
                                                        className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-b-0"
                                                    >
                                                        <div className="text-sm font-semibold text-slate-800">{member.full_name}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">{isReceivedType(type) ? 'From Member *' : 'To Member *'}</label>
                                    <div className="relative">
                                        <UserRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                        <input
                                            type="text"
                                            value={formData.to_member_name}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                setFormData((prev) => ({ ...prev, to_member_name: value, to_member_id: '' }));
                                                setShowMemberDropdown(true);
                                            }}
                                            onFocus={() => setShowMemberDropdown(true)}
                                            placeholder="Type member name..."
                                            className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                        />
                                        {memberSearchLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-slate-400" />}
                                    </div>
                                    {showMemberDropdown && memberResults.length > 0 && (
                                        <div className="mt-1 border border-slate-200 rounded-xl bg-white shadow-lg max-h-52 overflow-y-auto">
                                            {memberResults.map((member) => (
                                                <button
                                                    key={member._id}
                                                    type="button"
                                                    onClick={() => handleMemberSelect(member)}
                                                    className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-b-0"
                                                >
                                                    <div className="text-sm font-semibold text-slate-800">{member.full_name}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Business Given fields */}
                            {isBusinessType(type) && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Amount *</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={formData.amount}
                                            onChange={(event) => setFormData((prev) => ({ ...prev, amount: event.target.value }))}
                                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Date</label>
                                        <div className="relative">
                                            <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                            <input
                                                type="date"
                                                value={formData.date}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Remarks</label>
                                        <input
                                            value={formData.remarks}
                                            onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
                                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Reference Given fields */}
                            {isReferenceType(type) && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Reference Name</label>
                                            <input
                                                value={formData.refName}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, refName: event.target.value }))}
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Phone</label>
                                            <input
                                                value={formData.phone}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Date</label>
                                            <div className="relative">
                                                <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                                <input
                                                    type="date"
                                                    value={formData.date}
                                                    onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
                                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Remarks</label>
                                            <input
                                                value={formData.remarks}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* One-to-One fields */}
                            {type === 'one-to-one' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Meeting Date</label>
                                        <div className="relative">
                                            <Calendar className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                            <input
                                                type="date"
                                                value={formData.meeting_date}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, meeting_date: event.target.value }))}
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Location *</label>
                                        <div className="relative">
                                            <MapPin className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                                            <select
                                                value={formData.location}
                                                onChange={(event) => setFormData((prev) => ({ ...prev, location: event.target.value }))}
                                                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                            >
                                                <option value="my place">My Place</option>
                                                <option value="your place">Your Place</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    {formData.location === 'other' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Address *</label>
                                                <input
                                                    value={formData.address}
                                                    onChange={(event) => setFormData((prev) => ({ ...prev, address: event.target.value }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Latitude</label>
                                                <input
                                                    value={formData.latitude}
                                                    onChange={(event) => setFormData((prev) => ({ ...prev, latitude: event.target.value }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Longitude</label>
                                                <input
                                                    value={formData.longitude}
                                                    onChange={(event) => setFormData((prev) => ({ ...prev, longitude: event.target.value }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Remarks</label>
                                        <textarea
                                            rows="3"
                                            value={formData.remarks}
                                            onChange={(event) => setFormData((prev) => ({ ...prev, remarks: event.target.value }))}
                                            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Meeting Image</label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                        {meetingImagePreview ? (
                                            <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                                                <img src={meetingImagePreview} alt="preview" className="w-full h-36 rounded-lg object-cover border border-slate-200" />
                                                <div className="mt-3 flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700"
                                                    >
                                                        Change
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (meetingImagePreview && meetingImagePreview.startsWith('blob:')) {
                                                                URL.revokeObjectURL(meetingImagePreview);
                                                            }
                                                            setMeetingImageFile(null);
                                                            setMeetingImagePreview('');
                                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                                        }}
                                                        className="px-3 py-1.5 text-xs rounded-lg border border-red-200 bg-red-50 text-red-700"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50 py-6 text-sm text-slate-600 font-semibold inline-flex items-center justify-center gap-2"
                                            >
                                                <ImageIcon className="w-4 h-4" /> Upload Image
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 rounded-xl bg-blue-600 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    {submitting ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChapterNetworkingDetail;